import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import {
  ScanBarcode, Terminal, FolderOpen, RotateCcw,
  Wifi, WifiOff, Zap, Activity, ChevronRight
} from 'lucide-react';

export default function DashboardPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  const quickActions = [
    { icon: ScanBarcode, label: t('dashboard.generateBarcode'), desc: 'Create Code 128 configuration barcodes', color: '#3b82f6', to: '/barcode' },
    { icon: Terminal, label: t('dashboard.openSerial'), desc: 'Direct USB CDC communication', color: '#8b5cf6', to: '/serial' },
    { icon: FolderOpen, label: t('dashboard.viewProfiles'), desc: 'Manage saved scanner configurations', color: '#06b6d4', to: '/profiles' },
    { icon: RotateCcw, label: t('dashboard.factoryReset'), desc: 'Operand 0x43 — Reset all settings', color: '#ef4444', to: '/barcode?cmd=FACTORY_RESET' },
  ];

  const stats = [
    { label: 'Host Mode', value: 'HID', icon: Wifi, color: '#22c55e' },
    { label: 'Baud Rate', value: '9600', icon: Activity, color: '#f59e0b' },
    { label: 'Trigger', value: 'Manual', icon: Zap, color: '#8b5cf6' },
  ];

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold mb-1" style={{color:'var(--text-primary)'}}>
          {t('dashboard.welcome')}, <span className="text-brand-500">{user?.displayName}</span>
        </h1>
        <p style={{color:'var(--text-muted)'}}>SophistiScan SQ400H Configuration System</p>
      </div>

      {/* Scanner Status */}
      <div className="card p-4 md:p-5 mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{background:'var(--bg-card-hover)', border:'1px solid var(--border)'}}>
            <WifiOff className="w-6 h-6" style={{color:'var(--text-muted)'}} />
          </div>
          <div>
            <h3 className="font-semibold" style={{color:'var(--text-primary)'}}>{t('dashboard.scannerStatus')}</h3>
            <div className="flex items-center gap-2 mt-0.5">
              <div className="w-2 h-2 rounded-full bg-gray-400" />
              <span className="text-sm" style={{color:'var(--text-muted)'}}>{t('dashboard.disconnected')}</span>
            </div>
          </div>
        </div>
        <button onClick={() => navigate('/serial')} className="btn-secondary text-sm w-full sm:w-auto">{t('serial.connect')}</button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mb-8">
        {stats.map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="card p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${color}15`, border: `1px solid ${color}30` }}>
              <Icon className="w-5 h-5" style={{ color }} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider" style={{color:'var(--text-muted)'}}>{label}</p>
              <p className="text-lg font-semibold font-mono" style={{color:'var(--text-primary)'}}>{value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <h2 className="section-title flex items-center gap-2">
        <Zap className="w-5 h-5 text-brand-500" />
        {t('dashboard.quickActions')}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
        {quickActions.map(({ icon: Icon, label, desc, color, to }) => (
          <button key={to} onClick={() => navigate(to)} className="card-hover p-4 md:p-5 text-left group flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 transition-transform group-hover:scale-110"
              style={{ background: `${color}15`, border: `1px solid ${color}30` }}>
              <Icon className="w-6 h-6" style={{ color }} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold group-hover:text-brand-500 transition-colors" style={{color:'var(--text-primary)'}}>{label}</h3>
              <p className="text-sm mt-0.5" style={{color:'var(--text-muted)'}}>{desc}</p>
            </div>
            <ChevronRight className="w-5 h-5 shrink-0 hidden sm:block" style={{color:'var(--text-muted)'}} />
          </button>
        ))}
      </div>

      {/* Quick Reference */}
      <div className="mt-8 card p-4 md:p-6">
        <h3 className="font-semibold mb-3" style={{color:'var(--text-primary)'}}>Quick Reference — Key Operands</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
          {[
            { op: '0x41', desc: 'Host Mode' }, { op: '0x42', desc: 'Trigger Mode' },
            { op: '0x43', desc: 'Factory Reset' }, { op: '0x44', desc: 'Decode Timeout' },
            { op: '0x45', desc: 'Data Suffix' }, { op: '0x48', desc: 'Baud Rate' },
            { op: '0x52', desc: 'Data Prefix' }, { op: '0x5D', desc: 'Compound Cmd' },
          ].map(({ op, desc }) => (
            <div key={op} className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{background:'var(--bg-input)'}}>
              <code className="text-brand-500 font-mono text-sm font-bold">{op}</code>
              <span className="text-xs" style={{color:'var(--text-muted)'}}>{desc}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
